import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { StepData } from '@/components/atoms/Wizard/Wizard';
import { computeText } from '@/locales/utils';
import { useCreateBrandRestaurantMutation } from '@/services/innovorder/brand/brand.endpoints';
import { useLazyGetBrandUsersListQuery } from '@/services/innovorder/user/user.endpoints';
import { UserRoles } from '@/services/innovorder/user/user.type';
import { useGetVatConfigurationQuery } from '@/services/innovorder/vatConfiguration/vatConfiguration.endpoints';
import { useGetBrandOperationZoneStatusQuery } from '@/services/innovorder/brand/brand.endpoints';
import {
    useAssignOperationZoneMutation,
    useConfigureOperationZoneMutation,
} from '@/services/innovorder/operationZone/operationZone.endpoints';
import { useAuthCookieHandler } from '@/hooks/useAuthCookieHandler/useAuthCookieHandler';
import { getUseNewRestaurantInfoPage, getUseOperationZones } from '@/redux/launchDarkly/launchDarkly.slice';
import { redirectToBackofficeV1 } from '@/utils/backoffice-v1';
import { Currency } from '@/services/innovorder/brand/brand.type';
import { COUNTRY_FLAG_MAP } from '@/pages/Restaurant/OperationZones/OperationZones.type';
import type { Territory } from '@/pages/Restaurant/OperationZones/OperationZones.type';
import RestaurantInfoStep from './RestaurantInfoStep/RestaurantInfoStep';
import ManagerStep from './ManagerStep/ManagerStep';
import OperationZoneSelectStep from './OperationZoneSelectStep/OperationZoneSelectStep';
import OperationZoneProviderStep from './OperationZoneProviderStep/OperationZoneProviderStep';

type CreateRestaurantFormData = {
    name: string;
    phone: string;
    addressName: string;
    address: {
        route: string;
        postalCode: string;
        locality: string;
        country: string;
        streetNumber: string;
        administrativeAreaLevel1: string;
        administrativeAreaLevel2: string;
        provider: string;
        providerId: string;
        lat: number;
        lng: number;
    };
    locale: string;
    currency: string;
    operationZoneCode: string | null;
    operationZoneProvider: string | null;
    managerMode: 'existing' | 'new' | 'skip';
    userId?: number;
    user: {
        firstName: string;
        lastName: string;
        phone: string;
        email: string;
        password: string | null;
        letUserChoosePassword: boolean;
    };
};

type CreateRestaurantViewModel = {
    steps: StepData[];
    form: UseFormReturn<CreateRestaurantFormData>;
    onSubmit: (data: CreateRestaurantFormData) => void;
    isLoading: boolean;
    selectedStep: number | null;
    resetState: () => void;
};

const useCreateRestaurantViewModel = (brandId: string): CreateRestaurantViewModel => {
    const intl = useIntl();
    const history = useHistory();
    const { token } = useAuthCookieHandler();
    const useNewRestaurantInfoPage = useSelector(getUseNewRestaurantInfoPage);
    const operationZonesEnabled = useSelector(getUseOperationZones);

    const [selectedStep, setSelectedStep] = useState<number | null>(null);

    const { data: vatConfigData } = useGetVatConfigurationQuery({ token }, { skip: !token });

    const territories: Territory[] = useMemo(() => {
        if (!vatConfigData) return [];
        return vatConfigData.operationZones.map((zone) => ({
            id: zone.code,
            name: zone.name,
            code: zone.code,
            flag: COUNTRY_FLAG_MAP[zone.countryCode] ?? '\u{1F3F3}\u{FE0F}',
            solution: zone.fiscalityType.length > 1 ? 'operationZones.multiProvider' : zone.fiscalityType[0],
            availableProviders: zone.fiscalityType,
            countryCode: zone.countryCode,
            currencies: zone.currencies,
        }));
    }, [vatConfigData]);

    const { data: brandOpZoneStatus } = useGetBrandOperationZoneStatusQuery(
        { brandId: Number(brandId), token: token || '' },
        { skip: !token || !brandId || !operationZonesEnabled },
    );

    const brandOpZoneContext = useMemo(
        () => ({
            hasOpZoneRestaurant: brandOpZoneStatus?.hasOpZoneRestaurant ?? false,
            brandCurrency: brandOpZoneStatus?.brandCurrency ?? null,
        }),
        [brandOpZoneStatus],
    );

    const [getBrandUsers, { data: brandUsersData }] = useLazyGetBrandUsersListQuery();
    const brandUsers = useMemo(
        () =>
            (brandUsersData || []).map((u) => ({
                userId: u.userId,
                email: u.email,
                firstName: u.firstName,
                lastName: u.lastName,
            })),
        [brandUsersData],
    );

    useEffect(() => {
        if (token && brandId) {
            getBrandUsers({ brandId: Number(brandId), orderByLastConnection: false, role: UserRoles.restaurant });
        }
    }, [token, brandId, getBrandUsers]);

    const [createRestaurant, { isLoading: isCreating, reset: resetMutation }] = useCreateBrandRestaurantMutation();
    const [assignOperationZone, { isLoading: isAssigning }] = useAssignOperationZoneMutation();
    const [configureOperationZone, { isLoading: isConfiguring }] = useConfigureOperationZoneMutation();
    const isLoading = isCreating || isAssigning || isConfiguring;

    const resolver = yupResolver(
        Yup.object({
            name: Yup.string()
                .min(2, computeText(intl, 'field.minTwo'))
                .max(50)
                .required(computeText(intl, 'field.required')),
            phone: Yup.string()
                .test('validPhoneNumber', computeText(intl, 'brands.form.error.phone.valid'), (value) =>
                    value ? isValidPhoneNumber(value, 'FR') : false,
                )
                .required(computeText(intl, 'field.required')),
            addressName: Yup.string().required(computeText(intl, 'field.required')),
            address: Yup.object({
                lat: Yup.number().required(),
                lng: Yup.number().required(),
                providerId: Yup.string(),
            }),
            locale: Yup.string().required(computeText(intl, 'field.required')),
            currency: Yup.string().required(computeText(intl, 'field.required')),
            operationZoneCode: operationZonesEnabled
                ? Yup.string().required(computeText(intl, 'field.required'))
                : Yup.string().nullable(),
            operationZoneProvider: Yup.string()
                .nullable()
                .when('operationZoneCode', {
                    is: (code: string | null) => {
                        if (!code) return false;
                        const zone = territories.find((t) => t.code === code);
                        return (zone?.availableProviders.length ?? 0) > 1;
                    },
                    then: () => Yup.string().required(computeText(intl, 'field.required')),
                    otherwise: () => Yup.string().nullable(),
                }),
            managerMode: Yup.string().oneOf(['existing', 'new', 'skip']).required(),
            userId: Yup.number().when('managerMode', {
                is: 'existing',
                then: () => Yup.number().required(computeText(intl, 'field.required')),
                otherwise: () => Yup.number().nullable(),
            }),
            user: Yup.object().when('managerMode', {
                is: 'new',
                then: () =>
                    Yup.object({
                        firstName: Yup.string().required(computeText(intl, 'field.required')),
                        lastName: Yup.string().required(computeText(intl, 'field.required')),
                        phone: Yup.string()
                            .test('validPhoneNumber', computeText(intl, 'brands.form.error.phone.valid'), (value) =>
                                value ? isValidPhoneNumber(value, 'FR') : false,
                            )
                            .required(computeText(intl, 'field.required')),
                        email: Yup.string()
                            .email(computeText(intl, 'field.invalidEmail'))
                            .required(computeText(intl, 'field.required')),
                        letUserChoosePassword: Yup.boolean(),
                        password: Yup.string().when('letUserChoosePassword', {
                            is: true,
                            then: () => Yup.string().nullable(),
                            otherwise: () =>
                                Yup.string()
                                    .min(12, computeText(intl, 'brands.form.error.password.min'))
                                    .required(computeText(intl, 'field.required')),
                        }),
                    }),
                otherwise: () => Yup.object().nullable(),
            }),
        }),
    );

    const form = useForm<CreateRestaurantFormData>({
        resolver,
        mode: 'all',
        defaultValues: {
            name: '',
            phone: '',
            addressName: '',
            address: {
                route: '',
                postalCode: '',
                locality: '',
                country: '',
                streetNumber: '',
                administrativeAreaLevel1: '',
                administrativeAreaLevel2: '',
                provider: '',
                providerId: '',
                lat: 0,
                lng: 0,
            },
            locale: 'fr',
            currency: Currency.EUR,
            operationZoneCode: null,
            operationZoneProvider: null,
            managerMode: 'skip',
            user: {
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                password: '',
                letUserChoosePassword: true,
            },
        },
    });

    const { watch } = form;
    const name = watch('name');
    const phone = watch('phone');
    const addressName = watch('addressName');
    const selectedZoneCode = watch('operationZoneCode');
    const selectedZoneProvider = watch('operationZoneProvider');

    const isStep1Invalid = !name || !phone || !addressName;

    const selectedTerritory = useMemo(
        () => territories.find((t) => t.code === selectedZoneCode) ?? null,
        [territories, selectedZoneCode],
    );
    const needsProviderStep = (selectedTerritory?.availableProviders.length ?? 0) > 1;

    const navigateToRestaurant = useCallback(
        (restaurantId: number) => {
            if (useNewRestaurantInfoPage) {
                history.push(`/brands/${brandId}/restaurants/${restaurantId}/info`);
            } else {
                redirectToBackofficeV1(`/home/brands/${brandId}/restaurants/${restaurantId}/info`);
            }
        },
        [brandId, history, useNewRestaurantInfoPage],
    );

    const initialSteps: StepData[] = useMemo(() => {
        const steps: StepData[] = [
            {
                component: <RestaurantInfoStep />,
                title: computeText(intl, 'brandRestaurants.create.restaurantInfo'),
                isNextDisabled: isStep1Invalid,
            },
        ];

        if (operationZonesEnabled) {
            steps.push({
                component: (
                    <OperationZoneSelectStep
                        territories={territories}
                        brandCurrency={brandOpZoneContext.brandCurrency}
                        hasOpZoneRestaurant={brandOpZoneContext.hasOpZoneRestaurant}
                    />
                ),
                title: computeText(intl, 'brandRestaurants.create.operationZone'),
                isNextDisabled: !selectedZoneCode,
            });

            if (needsProviderStep) {
                steps.push({
                    component: <OperationZoneProviderStep territories={territories} />,
                    title: computeText(intl, 'brandRestaurants.create.operationZone.selectProvider'),
                    isNextDisabled: !selectedZoneProvider,
                });
            }
        }

        steps.push({
            component: <ManagerStep brandUsers={brandUsers} />,
            title: computeText(intl, 'brandRestaurants.create.manager'),
        });

        return steps;
    }, [
        intl,
        brandUsers,
        operationZonesEnabled,
        territories,
        selectedZoneCode,
        needsProviderStep,
        selectedZoneProvider,
        isStep1Invalid,
        brandOpZoneContext.brandCurrency,
        brandOpZoneContext.hasOpZoneRestaurant,
    ]);

    const [steps, setSteps] = useState<StepData[]>(initialSteps);

    // Indices in the filtered (visible) steps array — the Wizard filters out disabled steps
    const filteredManagerStepIndex = useMemo(() => {
        if (!operationZonesEnabled) return 1;
        return needsProviderStep ? 3 : 2;
    }, [operationZonesEnabled, needsProviderStep]);

    useEffect(() => {
        setSteps(initialSteps);
    }, [initialSteps]);

    const onSubmit = useCallback(
        async (data: CreateRestaurantFormData) => {
            const payload: Parameters<typeof createRestaurant>[0] = {
                brandId: Number(brandId),
                name: data.name,
                phone: data.phone,
                address: {
                    lat: data.address.lat,
                    lng: data.address.lng,
                    providerId: data.address.providerId,
                },
                locale: data.locale,
                token: token || '',
            };

            payload.currency = data.currency;

            if (data.managerMode === 'existing' && data.userId) {
                payload.userId = data.userId;
            } else if (data.managerMode === 'new') {
                payload.user = {
                    firstName: data.user.firstName,
                    lastName: data.user.lastName,
                    phone: data.user.phone,
                    email: data.user.email,
                    password: data.user.letUserChoosePassword ? null : data.user.password,
                    letUserChoosePassword: data.user.letUserChoosePassword,
                };
            }

            try {
                const { restaurantId } = await createRestaurant(payload).unwrap();

                if (data.operationZoneCode) {
                    try {
                        const { operationZoneId } = await assignOperationZone({
                            restaurantId,
                            operationZoneCode: data.operationZoneCode,
                            isPrimary: true,
                            token: token || '',
                        }).unwrap();

                        const zone = territories.find((t) => t.code === data.operationZoneCode);
                        const providers = zone?.availableProviders ?? [];
                        const provider = providers.length > 1 ? data.operationZoneProvider : providers[0] ?? null;

                        if (provider) {
                            await configureOperationZone({
                                restaurantId,
                                operationZoneId,
                                socialReason: '',
                                address: {
                                    name: data.addressName,
                                    route: data.address.route,
                                    postalCode: data.address.postalCode,
                                    locality: data.address.locality,
                                    country: data.address.country,
                                    streetNumber: data.address.streetNumber,
                                    administrativeAreaLevel1: data.address.administrativeAreaLevel1,
                                    administrativeAreaLevel2: data.address.administrativeAreaLevel2,
                                    provider: data.address.provider,
                                    providerId: data.address.providerId,
                                    lat: data.address.lat,
                                    lng: data.address.lng,
                                },
                                vatNumber: '',
                                siret: '',
                                provider,
                                token: token || '',
                            }).unwrap();
                        }
                    } catch {
                        // Zone assign/configure failed but restaurant was created — proceed to confirmation
                    }
                }

                navigateToRestaurant(restaurantId);
            } catch (err) {
                const fetchError = err as FetchBaseQueryError | undefined;
                const errorObject = fetchError?.data as { code: string; message: string } | undefined;
                if (errorObject?.code === 'duplicate_email' || errorObject?.code === 'invalid_parameters') {
                    form.setError('user.email', { message: errorObject.message });
                    setSelectedStep(filteredManagerStepIndex);
                }
                if (errorObject?.code === 'invalid_password') {
                    form.setError('user.password', { message: errorObject.message });
                    setSelectedStep(filteredManagerStepIndex);
                }
            }
        },
        [
            brandId,
            token,
            createRestaurant,
            assignOperationZone,
            configureOperationZone,
            territories,
            navigateToRestaurant,
            filteredManagerStepIndex,
            form,
        ],
    );

    useEffect(() => {
        if (selectedStep != null) {
            setSelectedStep(null);
        }
    }, [selectedStep]);

    const resetState = useCallback(() => {
        form.reset();
        resetMutation();
        setSteps(initialSteps);
        setSelectedStep(null);
    }, [form, resetMutation, initialSteps]);

    return { steps, form, onSubmit, isLoading, selectedStep, resetState };
};

export default useCreateRestaurantViewModel;
